import { readBeginnerTimeMilliseconds } from '@skyrover/blockly-blocks';
import type { Block } from 'blockly/core';

import { arduinoGenerator, blockTraceComment, Order } from './arduino-generator.js';
import { getEveryBlockIndex } from './beginner-time-state.js';

const TIMER_START_VAR = 'skyroverTimerStart';

function statementPrefix(block: Block, code: string): string {
  return `${blockTraceComment(block)}\n${code}`;
}

function ensureTimerSupport(): void {
  arduinoGenerator.addGlobal(`unsigned long ${TIMER_START_VAR} = 0;`);
  arduinoGenerator.addSetupOnce('beginner-timer-start', `${TIMER_START_VAR} = millis();`);
}

function timeUnit(block: Block, fieldName = 'UNIT'): 'SECONDS' | 'MILLISECONDS' {
  return String(block.getFieldValue(fieldName) ?? 'SECONDS') === 'MILLISECONDS'
    ? 'MILLISECONDS'
    : 'SECONDS';
}

/** Elapsed millis since program start, optionally as seconds (float). */
function currentTimeExpr(unit: 'SECONDS' | 'MILLISECONDS'): string {
  return unit === 'MILLISECONDS' ? 'millis()' : '(millis() / 1000.0)';
}

/** Elapsed millis since timer reset, optionally as seconds (float). */
function timerValueExpr(unit: 'SECONDS' | 'MILLISECONDS'): string {
  return unit === 'MILLISECONDS'
    ? `(millis() - ${TIMER_START_VAR})`
    : `((millis() - ${TIMER_START_VAR}) / 1000.0)`;
}

function everyLastVar(block: Block): string {
  return `skyroverEveryLast_${getEveryBlockIndex(block.id)}`;
}

export function registerBeginnerTimeGenerators(): void {
  arduinoGenerator.forBlock['beginner_wait_until'] = (block: Block) => {
    const condition = arduinoGenerator.valueToCode(block, 'CONDITION', Order.NONE) || 'false';
    return statementPrefix(block, `while (!(${condition})) {\n}\n`);
  };

  arduinoGenerator.forBlock['beginner_current_time'] = (block: Block) => {
    const unit = timeUnit(block);
    return [currentTimeExpr(unit), Order.FUNCTION_CALL];
  };

  arduinoGenerator.forBlock['beginner_reset_timer'] = (block: Block) => {
    ensureTimerSupport();
    return statementPrefix(block, `${TIMER_START_VAR} = millis();\n`);
  };

  arduinoGenerator.forBlock['beginner_timer_value'] = (block: Block) => {
    ensureTimerSupport();
    const unit = timeUnit(block);
    return [timerValueExpr(unit), Order.ADDITIVE];
  };

  arduinoGenerator.forBlock['beginner_has_time_passed'] = (block: Block) => {
    ensureTimerSupport();
    const durationMs = Math.max(0, readBeginnerTimeMilliseconds(block, 'DURATION'));
    return [
      `((millis() - ${TIMER_START_VAR}) >= ${durationMs}UL)`,
      Order.RELATIONAL,
    ];
  };

  arduinoGenerator.forBlock['beginner_every'] = (block: Block) => {
    const durationMs = Math.max(1, readBeginnerTimeMilliseconds(block, 'DURATION'));
    const branch = arduinoGenerator.statementToCode(block, 'DO');
    const lastVar = everyLastVar(block);
    arduinoGenerator.addGlobal(`unsigned long ${lastVar} = 0;`);

    return statementPrefix(
      block,
      [
        `if (millis() - ${lastVar} >= ${durationMs}UL) {`,
        `  ${lastVar} = millis();`,
        branch || '',
        `}`,
        '',
      ].join('\n'),
    );
  };
}

registerBeginnerTimeGenerators();
